import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Bus, Plus, RefreshCw, Trash2, Eye, CheckCircle2, Clock,
  Loader2, Search, X, FileUp, Check,
} from "lucide-react";
import BulkDocsUploadDialog from "./BulkDocsUploadDialog";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); }
  catch { return iso; }
};

const choiceLabel = (c) => {
  if (!c) return null;
  if (c.opcao === "sim") {
    const tipos = { municipal: "Municipal", intermunicipal: "Intermunicipal",
                    empresa: "Fornecido pela empresa" };
    return `SIM — ${tipos[c.tipo_conducao] || c.tipo_conducao}`;
  }
  if (c.opcao === "nao") {
    const motivos = { proprio: "Transporte próprio",
                      residencia: "Residência próxima",
                      outros: "Outros" };
    return `NÃO — ${motivos[c.motivo_recusa] || c.motivo_recusa}`;
  }
  return null;
};


function CreateVTDialog({ open, onOpenChange, onCreated, adminToken }) {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [cidade, setCidade] = useState("Salvador/BA");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId(""); setSearch(""); setCidade("Salvador/BA");
    axios.get(`${API}/admin/employees`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      params: { limit: 1000 },
    }).then((r) => {
      setEmployees(Array.isArray(r.data) ? r.data
                   : (r.data?.employees || []));
    }).catch(() => setEmployees([]));
  }, [open, adminToken]);

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.nome || "").toLowerCase().includes(q)
        || (e.matricula || "").toLowerCase().includes(q)
        || (e.cpf || "").includes(q);
  }).slice(0, 30);

  const selected = employees.find((e) => e.id === employeeId);

  const submit = async () => {
    if (!employeeId) return toast.error("Selecione um funcionário");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/vt-declarations/create`,
        { employee_id: employeeId, cidade_assinatura: cidade.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 30000 });
      toast.success("Declaração de VT criada");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw]" data-testid="vt-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-blue-700" />
            Nova Declaração de Vale Transporte
          </DialogTitle>
          <DialogDescription>
            O colaborador escolherá SIM ou NÃO e assinará eletronicamente via OTP.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Funcionário</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Buscar por nome, matrícula ou CPF…"
                className="pl-9 bg-white" value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="vt-employee-search" />
            </div>
            {selected ? (
              <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 p-2">
                <div className="text-xs">
                  <div className="font-semibold text-emerald-900">{selected.nome}</div>
                  <div className="text-emerald-700 font-mono">Mat. {selected.matricula} · CPF {selected.cpf}</div>
                </div>
                <button onClick={() => setEmployeeId("")}
                  className="text-emerald-700 hover:text-emerald-900">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white">
                {filtered.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    {search ? "Nenhum encontrado." : "Carregando…"}
                  </div>
                ) : filtered.map((e) => (
                  <button key={e.id} onClick={() => setEmployeeId(e.id)}
                    className="w-full text-left p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-xs"
                    data-testid={`vt-employee-pick-${e.matricula}`}>
                    <div className="font-medium">{e.nome}</div>
                    <div className="text-slate-500 font-mono">Mat. {e.matricula} · CPF {e.cpf}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Cidade da assinatura</Label>
            <Input value={cidade} onChange={(e) => setCidade(e.target.value)}
              placeholder="Salvador/BA" data-testid="vt-cidade-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="vt-create-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bus className="w-4 h-4 mr-2" />}
            Gerar declaração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminVTDeclarationsTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vt-declarations`, {
        headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000,
      });
      setRows(r.data || []);
    } catch { toast.error("Falha ao carregar"); }
    finally { setLoading(false); }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.employee_nome || "").toLowerCase().includes(q)
        || (r.employee_matricula || "").toLowerCase().includes(q);
  });

  const openPreview = async (row) => {
    try {
      const r = await axios.get(`${API}/admin/vt-declarations/${row.id}/preview`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: "blob", timeout: 30000,
      });
      setPreviewUrl(URL.createObjectURL(r.data));
      setPreviewName(`Vale Transporte — ${row.employee_nome || row.id.slice(0, 8)}`);
    } catch { toast.error("Não foi possível abrir o PDF"); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setPreviewName("");
  };

  const remove = async (row) => {
    if (!window.confirm(`Excluir declaração de VT de ${row.employee_nome}?`)) return;
    try {
      await axios.delete(`${API}/admin/vt-declarations/${row.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Excluída"); load();
    } catch { toast.error("Falha ao excluir"); }
  };

  const pending = rows.filter((r) => r.status !== "signed").length;
  const signed = rows.filter((r) => r.status === "signed").length;
  const optedIn = rows.filter((r) => r.choice?.opcao === "sim").length;
  const optedOut = rows.filter((r) => r.choice?.opcao === "nao").length;

  return (
    <div className="space-y-4" data-testid="admin-vt-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Bus className="w-5 h-5 text-blue-700" />
            Vale Transporte
          </h2>
          <p className="text-xs text-slate-500">
            Declaração de opção pelo benefício do VT.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="vt-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}
            data-testid="vt-bulk-upload-btn">
            <FileUp className="w-4 h-4 mr-2" />Importar PDF
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="vt-new-btn">
            <Plus className="w-4 h-4 mr-2" />Nova Declaração
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[10px] text-slate-500 uppercase font-bold">Total</div>
          <div className="text-2xl font-bold text-slate-900">{rows.length}</div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] text-amber-700 uppercase font-bold">Pendentes</div>
          <div className="text-2xl font-bold text-amber-900">{pending}</div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] text-emerald-700 uppercase font-bold">Assinadas</div>
          <div className="text-2xl font-bold text-emerald-900">{signed}</div>
        </div>
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <div className="text-[10px] text-blue-700 uppercase font-bold">Optaram SIM</div>
          <div className="text-2xl font-bold text-blue-900">{optedIn}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] text-slate-600 uppercase font-bold">Optaram NÃO</div>
          <div className="text-2xl font-bold text-slate-900">{optedOut}</div>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Filtrar por nome ou matrícula…" className="pl-9"
          value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Funcionário</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-center">Escolha</th>
              <th className="px-3 py-2 text-center">Criada</th>
              <th className="px-3 py-2 text-center">Assinada</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  {rows.length === 0 ? 'Nenhuma. Clique "Nova Declaração" acima.' : "Sem resultados."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`vt-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.employee_nome || "—"}</div>
                  <div className="text-[10px] text-slate-500 font-mono">Mat. {r.employee_matricula || "—"}</div>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === "signed" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Assinada
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />Pendente
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {choiceLabel(r.choice) ? (
                    <span className={r.choice.opcao === "sim"
                      ? "text-emerald-700 font-semibold" : "text-slate-700 font-semibold"}>
                      {choiceLabel(r.choice)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">{fmtDateTime(r.created_at)}</td>
                <td className="px-3 py-2 text-center text-[10px]">{r.signed_at ? fmtDateTime(r.signed_at) : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(r)} data-testid={`vt-preview-${r.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`vt-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>{previewName}</DialogTitle></DialogHeader>
          {previewUrl && (
            <iframe src={previewUrl} title="VT preview"
              className="flex-1 w-full rounded-md border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>

      <CreateVTDialog open={createOpen} onOpenChange={setCreateOpen}
        onCreated={load} adminToken={adminToken} />

      <BulkDocsUploadDialog open={bulkOpen} onOpenChange={setBulkOpen}
        kind="vt" adminToken={adminToken}
        title="Importar Declarações de VT do Folhamatic"
        accentClassName="bg-blue-700 hover:bg-blue-800"
        onConfirmed={load} />
    </div>
  );
}
